/**
 * @ngdoc Filter
 * @name navFilter
 */
import { hoursStringToSecondsNumber } from '@/vue/helpers/handleTime';
import { mapTankTypes, mapGeneratorStates } from '@/angular/static/installationsFilterBarMaps';
import {
    getAttributeInstances,
    getAttributeInstanceValue,
    applyMinMaxFilter,
    applyMinMaxFilterPositives,
} from '@/angular/utils/installationOverviewHelpers';
import moment from 'moment';

(function() {
    'use strict';

    angular.module('vrm.core')
        .filter('installationOverviewFilter', installationOverviewFilter);

    /**
     * @constructor
     * @ngInject
     */
    function installationOverviewFilter(vrmConfig, locale) {
        return function(installations, overviewSearch, hasAlarm, filterByLocation=true){
            // Array of installations
            let output = _.toArray(installations);

            // filter by enhanced search
            output = _filterByEnhancedSearch(output, overviewSearch);

            // filter by tags
            output = _filterByTags(output, overviewSearch);

            // filter by battery
            output = _filterByBattery(output, overviewSearch);

            // filter by solar
            output = _filterBySolar(output, overviewSearch);

            // filter by load
            output = _filterByLoad(output, overviewSearch);

            // filter by grid
            output = _filterByGrid(output, overviewSearch);

            // filter by generator
            output = _filterByGenerator(output, overviewSearch);

            // filter by tank
            output = _filterByTank(output, overviewSearch);

            // filter by temperature
            output = _filterByTemperature(output, overviewSearch);

            // filter by last update
            output = _filterByLastUpdate(output, overviewSearch);

            // filter by term
            output = _filterByTerm(output, overviewSearch);

            // filter by alarm
            output = _filterByHasAlarm(output, hasAlarm);

            // filter by location
            output = _filterByLocation(output, overviewSearch, filterByLocation);

            // Filter by status
            output = _filterByStatus(output, overviewSearch);

            return output;
        };

        /**
         * Filter by enhanced search
         *
         * @param output
         * @param overviewSearch
         * @private
         */
         function _filterByEnhancedSearch(output, overviewSearch) {
             if(overviewSearch.searchResults.length === 0) return output;
             return output.filter(installation => overviewSearch.searchResults.includes(installation.id));
         }

        /**
         * Filter by map viewport
         *
         * @param output - list of installations
         * @param overviewSearch
         * @private
         */
        function _filterByLocation(output, overviewSearch, filterByLocation) {
            if (overviewSearch.location === null) return output;
            if (!filterByLocation) return output;

            return _.filter(output, function(installation) {
                return overviewSearch.location.includes(installation.id);
            });
        }

        /**
         * Sort array by name property
         * @param a
         * @param b
         * @returns {number}
         * @private
         */
        function _sortByName(a,b) {
            if (a.name < b.name)
                return -1;
            else if (a.name > b.name)
                return 1;
            else
                return 0;
        }

        /**
         * Filter by term
         *
         * @param output - list of installations
         * @param overviewSearch
         * @private
         */
        function _filterByTerm(output, overviewSearch) {
            if (overviewSearch.term.length === 0) return output;

            return _.filter(output, function(installation) {
                return installation.name ? (installation.name.toString().toLowerCase().indexOf(overviewSearch.term.toLowerCase()) > -1) : false;
            });
        }

        /**
         * Filter by tags
         *
         * @param output - list of installations
         * @param overviewSearch
         * @private
         */
        function _filterByTags(output, overviewSearch) {
            if(overviewSearch.tags && overviewSearch.tags.tags && overviewSearch.tags.tags.length){
                output = output.filter(installation => {
                    if(!installation.tags) {
                        return false;
                    }
                    return installation.tags.some(tag => {
                        return overviewSearch.tags.tags.includes(tag.idTag.toString())
                    })
                })
            }
            if(overviewSearch.groups && overviewSearch.groups.groups && overviewSearch.groups.groups.length){
                output = output.filter(installation => {
                    if(!installation.tags) {
                        return false;
                    }
                    return installation.tags.some(tag => {
                        return overviewSearch.groups.groups.includes(tag.idTag.toString())
                    })
                })
            }
            if(overviewSearch.alarm && overviewSearch.alarm.tags && overviewSearch.alarm.tags.length){
                output = output.filter(installation => {
                    if(!installation.alarm_tags) {
                        return false;
                    }
                    return installation.alarm_tags.some(tag => {
                        return overviewSearch.alarm.tags.includes(tag.toString())
                    })
                })
            }
            if(overviewSearch.teams && overviewSearch.teams.team && overviewSearch.teams.team.length){
                output = output.filter(installation => {
                    if(!installation.tags) {
                        return false;
                    }
                    return installation.tags.some(tag => {
                        return overviewSearch.teams.team.includes(tag.idTag.toString().trim())
                    })
                })
            }
            return output;
        }

        /**
         * Filter by battery
         *
         * @param output - list of installations
         * @param overviewSearch
         * @private
         */
        function _filterByBattery(output, overviewSearch) {
            if(!overviewSearch.battery){
                return output;
            }
            if (overviewSearch.battery.state.length !== 0) {
                output = output.filter(installation =>
                    installation.data
                    && installation.data.bst
                    && overviewSearch.battery.state.includes(installation.data.bst.textValue)
                )
            } // check

            output = applyMinMaxFilter(overviewSearch.battery.soc.min, overviewSearch.battery.soc.max, output, "bs"); // slider

            output = applyMinMaxFilter(overviewSearch.battery.voltage.min, overviewSearch.battery.voltage.max, output, "bv"); // minMax

            return  output
        }

        /**
         * Filter by solar
         *
         * @param output - list of installations
         * @param overviewSearch
         * @private
         */
        function _filterBySolar(output, overviewSearch) {
            if(!overviewSearch.solar){
                return output;
            }
            return applyMinMaxFilter(overviewSearch.solar.solar.min, overviewSearch.solar.solar.max, output, "solar_yield"); // minMax
        }

        /**
         * Filter by load
         *
         * @param output - list of installations
         * @param overviewSearch
         * @private
         */
        function _filterByLoad(output, overviewSearch) {
            if(!overviewSearch.load){
                return output;
            }
            return applyMinMaxFilter(overviewSearch.load.load.min, overviewSearch.load.load.max, output, "consumption"); // minMax
        }

        /**
         * Filter by grid
         *
         * @param output - list of installations
         * @param overviewSearch
         * @private
         */
        function _filterByGrid(output, overviewSearch) {
            if(!overviewSearch.grid){
                return output;
            }
            return applyMinMaxFilter(overviewSearch.grid.grid.min, overviewSearch.grid.grid.max, output, "from_to_grid"); // minMax
        }

        /**
         * Filter by generator
         *
         * @param output - list of installations
         * @param overviewSearch
         * @private
         */
        function _filterByGenerator(output, overviewSearch) {
            if(!overviewSearch.generator){
                return output;
            }
            if (overviewSearch.generator.state.length !== 0) {
                let possibleStates = []
                overviewSearch.generator.state.forEach((state) => {
                    possibleStates = possibleStates.concat(mapGeneratorStates[state])
                })
                output = output.filter(installation => {
                    if(installation.data && installation.data.gRC ){
                        return possibleStates.includes(installation.data.gRC.rawValue);
                    }
                    return false;
                })
            } // check

            output = applyMinMaxFilter(
                overviewSearch.generator.generator.min,
                overviewSearch.generator.generator.max,
                output,
                "generator"
            ); // minMax

            const minRuntime = overviewSearch.generator.runtime.min !== ""
                ? hoursStringToSecondsNumber(overviewSearch.generator.runtime.min)
                : false;
            const maxRuntime = overviewSearch.generator.runtime.max !== ""
                ? hoursStringToSecondsNumber(overviewSearch.generator.runtime.max)
                : false;
            output = applyMinMaxFilterPositives(minRuntime, maxRuntime, output, "gasd"); // minMax

            if(
                overviewSearch.runningSince === undefined || // installation overview doesn't have the runningSince values
                !overviewSearch.generator || // check if we have generator
                !overviewSearch.generator.running // check if we have running
            ) {
                return output;
            }

            if(overviewSearch.generator.running.min !== "" && overviewSearch.generator.running.max !== ""){
                const minRunning = hoursStringToSecondsNumber(overviewSearch.generator.running.min);
                const maxRunning = hoursStringToSecondsNumber(overviewSearch.generator.running.max);

                output = output.filter(installation => {
                    const runningSince = overviewSearch.runningSince[installation.idSite]
                    if(!runningSince) {
                        return false;
                    }
                    const difference = runningSince.end - runningSince.start
                    return minRunning <= difference && difference <= maxRunning
                })
            }
            else if(overviewSearch.generator.running.max !== ""){
                const maxRunning = hoursStringToSecondsNumber(overviewSearch.generator.running.max);

                output = output.filter(installation => {
                    const runningSince = overviewSearch.runningSince[installation.idSite]
                    if(!runningSince) {
                        return false;
                    }
                    const difference = runningSince.end - runningSince.start
                    return difference <= maxRunning
                })
            }
            else if(overviewSearch.generator.running.min !== ""){
                const minRunning = hoursStringToSecondsNumber(overviewSearch.generator.running.min);

                output = output.filter(installation => {
                    const runningSince = overviewSearch.runningSince[installation.idSite]
                    if(!runningSince) {
                        return false;
                    }
                    const difference = runningSince.end - runningSince.start
                    return minRunning <= difference
                })
            }

            return output
        }

        /**
         * Filter by tank
         *
         * @param output - list of installations
         * @param overviewSearch
         * @private
         */
        function _filterByTank(output, overviewSearch) {
            if(!overviewSearch.tank){
                return output;
            }

            if (overviewSearch.tank.type.length !== 0) {
                output = output.filter(
                    installation =>
                        getAttributeInstances(installation, "tl") &&
                        overviewSearch.tank.type.find(type => {
                            let keys = Object.keys(installation.data.tl.instances);
                            return keys.some(key => {
                                const tf = getAttributeInstanceValue(installation, "tf", key);
                                if (type === "Others" &&
                                    !(tf && tf.rawValue)
                                ) {
                                    return true;
                                }
                                return tf && mapTankTypes[type].includes(tf.rawValue);
                            });
                        })
                );
            }

            const hasMin = overviewSearch.tank.level.min !== "";
            const hasMax = overviewSearch.tank.level.max !== "";
            if (hasMin || hasMax) {
                output = output.filter(
                    installation =>{
                        if (!getAttributeInstances(installation, "tl")) return false;
                        return Object.keys(installation.data.tl.instances).some(key => {
                            const tl = getAttributeInstanceValue(installation, "tl", key);
                            if (!tl || !tl.rawValue) return false;
                            const value = parseFloat(tl.rawValue);
                            if (hasMin && hasMax){
                                return value >= parseFloat(overviewSearch.tank.level.min) && value <= parseFloat(overviewSearch.tank.level.max);
                            }
                            if (hasMin){
                                return value >= parseFloat(overviewSearch.tank.level.min);
                            }
                            if (hasMax){
                                return value <= parseFloat(overviewSearch.tank.level.max);
                            }
                            return false;
                        })
                    }
                );
            }

            return  output
        }

        /**
         * Filter by temperature
         *
         * @param output - list of installations
         * @param overviewSearch
         * @private
         */
        function _filterByTemperature(output, overviewSearch) {
            if(!overviewSearch.temperature){
                return output;
            }

            if (overviewSearch.temperature.type.length !== 0) {
                output = output.filter(installation =>
                    installation.data
                    && installation.data.tsT
                    && installation.data.tsT.instances
                    && overviewSearch.temperature.type.find( type => {
                        const keys = Object.keys(installation.data.tsT.instances);
                        if(type === "Others"){
                            return keys.some(key => installation.data.tst
                                && installation.data.tst.instances
                                && installation.data.tst.instances[key]
                                && installation.data.tst.instances[key].textValue === null)
                        }
                        return keys.some(key => installation.data.tst
                            && installation.data.tst.instances
                            && installation.data.tst.instances[key]
                            && installation.data.tst.instances[key].textValue === type)
                    })
                )
            }


            const hasMin = overviewSearch.temperature.temperature.min !== "";
            const hasMax = overviewSearch.temperature.temperature.max !== "";
            if (hasMin || hasMax) {
                output = output.filter(
                    installation =>{
                        if (!getAttributeInstances(installation, "tsT")) return false;
                        return Object.keys(installation.data.tsT.instances).some(key => {
                            const tsT = getAttributeInstanceValue(installation, "tsT", key);
                            if (!tsT || !tsT.rawValue) return false;
                            const value = parseFloat(tsT.rawValue);
                            if (hasMin && hasMax){
                                return value >= parseFloat(overviewSearch.temperature.temperature.min)
                                    && value <= parseFloat(overviewSearch.temperature.temperature.max);
                            }
                            if (hasMin){
                                return value >= parseFloat(overviewSearch.temperature.temperature.min);
                            }
                            if (hasMax){
                                return value <= parseFloat(overviewSearch.temperature.temperature.max);
                            }
                            return false;
                        })
                    }
                );
            }

            return  output
        }

        /**
         * Filter by temperature
         *
         * @param output - list of installations
         * @param overviewSearch
         * @private
         */
        function _filterByLastUpdate(output, overviewSearch) {
            if (overviewSearch.last_timestamp && overviewSearch.last_timestamp.time !== '') {
                let deltaTime = moment().subtract(1, 'y'); // Less than a year ago
                if (overviewSearch.last_timestamp.time.includes("plus") && overviewSearch.last_timestamp.time.includes("year")){
                    return output.filter(installation => {
                        const timestamp = moment(installation.last_timestamp * 1000);
                        return timestamp.isBefore(deltaTime);
                    });
                }
                else if (overviewSearch.last_timestamp.time.includes("month")){
                    deltaTime = moment().subtract(1, 'M');
                }
                else if (overviewSearch.last_timestamp.time.includes("week")){
                    deltaTime = moment().subtract(1, 'w');
                }
                else if (overviewSearch.last_timestamp.time.includes("day")){
                    deltaTime = moment().subtract(1, 'd');
                }
                else if (overviewSearch.last_timestamp.time.includes("hour")){
                    deltaTime = moment().subtract(1, 'h');
                }
                else if (overviewSearch.last_timestamp.time.includes("minute")){
                    deltaTime = moment().subtract(1, 'm');
                }

                return output.filter(installation => {
                    const timestamp = moment(installation.last_timestamp * 1000);
                    return deltaTime.isBefore(timestamp);
                })
            }
            return  output
        }

        /**
         * Filter by alarms
         *
         * @param output - list of installations
         * @param hasAlarm
         * @private
         */
        function _filterByHasAlarm(output, hasAlarm) {
            if (hasAlarm === undefined) return output;

            return  _.filter(output, function(installation) {
                return hasAlarm ? installation.alarm : !installation.alarm;
            });
        }

        /**
         * Filter by installation status
         *
         * Checks for alarm, warning, ok
         *
         * @param output - list of installations
         * @param overviewSearch
         * @private
         */
        function _filterByStatus(output, overviewSearch) {
            const filters = overviewSearch.status;

            if (!filters) {
                return output;
            }

            return  _.filter(output, function(installation) {
                return filters.includes(installation.status);
            });
        }
    }
})();
